import { useEffect, useState, useRef } from 'react'
import { gql, useQuery } from '@apollo/client'

const GetStudentQuery = gql`
  query GetStudentByIdQuery($studentId: ID!) {
    student(id: $studentId) {
      id
      firstName
      lastName
      golferProfile {
        id
      }
    }
  }
`

const ProductByPaymentIntentId = gql`
  query ProductByPaymentIntentId($paymentIntentId: String!) {
    productByPaymentIntentId(paymentIntentId: $paymentIntentId) {
      ... on LessonPack {
        id
        expiresAt
        quantity
        priceInCents
        registrationPayments {
          cardBrand
          last4
        }
        lessonType {
          id
          title
          durationInMinutes
          priceInCents
          giftingEnabled
          coach {
            name
            bookingUrl
            coachProfile {
              slug
              profilePhoto
            }
          }
          facilities {
            id
            name
            city
            state
            timezone
          }
        }
      }
      ... on PracticeSession {
        id
        practiceSessionType {
          id
          title
          description
          durationInMinutes
          priceInCents
          salesTaxInCents
          spaces {
            name
            facility {
              name
              city
              state
              timezone
            }
          }
        }
        reservation {
          startDateTime
          endDateTime
          student {
            firstName
            lastName
          }
        }
        transactions {
          registrationPayment {
            cardBrand
            last4
          }
        }
      }
      ... on Enrollment {
        lesson {
          title
          coachFacility {
            name
          }
        }
        student {
          contact {
            email
          }
        }
      }
    }
  }
`

const useGetProductByPaymentIntentId = (paymentIntentId, options = {}) => {
  const { maxRetries = 5, retryDelay = 1000 } = options
  const [product, setProduct] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [retryCount, setRetryCount] = useState(0)
  const retryTimeoutRef = useRef(null)
  const isRetryingRef = useRef(false)
  const queryResult = useQuery(ProductByPaymentIntentId, {
    variables: { paymentIntentId },
    skip: !paymentIntentId,
    errorPolicy: 'all',
  })

  const {
    data,
    loading: productLoading,
    error: queryError,
    refetch,
  } = queryResult

  // Reset retry state when paymentIntentId changes
  useEffect(() => {
    setRetryCount(0)
    setError(null)
    isRetryingRef.current = false
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }
  }, [paymentIntentId])

  useEffect(() => {
    setLoading(productLoading)
  }, [productLoading])

  useEffect(() => {
    if (data?.productByPaymentIntentId) {
      // For now, use the first product for backwards compatibility
      // TODO: Update UI to handle multiple products (e.g., group event enrollments)
      setProduct(data.productByPaymentIntentId[0])
      // Reset retry count on successful fetch
      setRetryCount(0)
      setError(null)
      setLoading(false)
      isRetryingRef.current = false
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
        retryTimeoutRef.current = null
      }
    }
  }, [data])

  useEffect(() => {
    if (
      !paymentIntentId ||
      isRetryingRef.current ||
      retryCount >= maxRetries ||
      !queryError
    ) {
      if (queryError && retryCount >= maxRetries) {
        setLoading(false)
        setError(queryError.graphQLErrors?.[0] || queryError)
      }
      return
    }

    // Mark that we're starting a retry
    isRetryingRef.current = true
    setLoading(true)

    retryTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await refetch()
        if (result.data?.productByPaymentIntentId) {
          setRetryCount(0)
        } else {
          setRetryCount(prev => prev + 1)
        }
      } catch {
        setRetryCount(prev => prev + 1)
      } finally {
        isRetryingRef.current = false
        setLoading(false)
      }
    }, retryDelay)

    return () => clearTimeout(retryTimeoutRef.current)
  }, [queryError, retryCount, maxRetries, retryDelay, refetch, paymentIntentId])
  return { product, loading, error }
}

export { GetStudentQuery, useGetProductByPaymentIntentId }
